import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { assistApi, STAGE5_LABELS } from './assistApi';
import { Field, SaveBar, useSave } from './AssistFormsA';

export function Stage5Form({ userId, brand, data, notify, onSaved }) {
  const L = STAGE5_LABELS[brand];
  const [f, setF] = useState({
    project_name: data.project_name || '', project_address: data.project_address || '', installation_date: data.installation_date || '',
    square_metres: data.square_metres || '', material_used: data.material_used || '', mix_or_blend: data.mix_or_blend || '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [busy, save] = useSave(() => assistApi.stage5(userId, brand, { ...f, square_metres: Number(f.square_metres), notify }), onSaved, 'First installation saved & marked complete');
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} className="grid sm:grid-cols-2 gap-3" data-testid="assist-stage5-form">
      <Field label="Project name"><Input required value={f.project_name} onChange={set('project_name')} data-testid="assist-s5-project" /></Field>
      <Field label="Project address"><Input required value={f.project_address} onChange={set('project_address')} /></Field>
      <Field label="Installation date"><Input required type="date" value={f.installation_date} onChange={set('installation_date')} data-testid="assist-s5-date" /></Field>
      <Field label="Square metres"><Input required type="number" min="0.1" step="0.1" value={f.square_metres} onChange={set('square_metres')} data-testid="assist-s5-sqm" /></Field>
      <Field label={L.material}><Input required value={f.material_used} onChange={set('material_used')} data-testid="assist-s5-material" /></Field>
      <Field label={L.blend}><Input required value={f.mix_or_blend} onChange={set('mix_or_blend')} data-testid="assist-s5-blend" /></Field>
      <div className="sm:col-span-2"><SaveBar busy={busy} testId="assist-s5-save" /></div>
    </form>
  );
}

const CATS = [['before', 'Before'], ['preparation', 'Preparation'], ['during', 'During'], ['finished', 'Finished'], ['video', 'Video (optional)']];

export function Stage6Form({ userId, brand, data, notify, onSaved }) {
  const [busyCat, setBusyCat] = useState(null);
  const photos = data.photos || [];
  const upload = async (cat, file) => {
    if (!file) return;
    setBusyCat(cat);
    try {
      const fd = new FormData(); fd.append('category', cat); fd.append('file', file);
      onSaved(await assistApi.stage6Photo(userId, brand, fd)); toast.success(`${cat} uploaded`);
    } catch (e) { toast.error(e.message); } finally { setBusyCat(null); }
  };
  const [busy, complete] = useSave(() => assistApi.stage6Complete(userId, brand, { notify }), onSaved, 'Documentation marked complete');
  const missing = CATS.filter(([c]) => c !== 'video' && !photos.some((p) => p.category === c)).map(([, l]) => l);
  return (
    <div className="space-y-3" data-testid="assist-stage6-form">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {CATS.map(([cat, label]) => {
          const existing = cat === 'video' ? (data.video_gridfs_id ? { gridfs_id: data.video_gridfs_id, filename: data.video_filename } : null) : photos.find((p) => p.category === cat);
          return (
            <label key={cat} className={`rounded-lg border p-2 text-center cursor-pointer text-xs ${existing ? 'border-emerald-300 bg-emerald-50' : 'border-dashed border-gray-300 hover:bg-gray-50'}`} data-testid={`assist-s6-${cat}`}>
              {existing && cat !== 'video' ? <img src={assistApi.photoUrl(brand, existing.gridfs_id)} alt={label} className="w-full h-16 object-cover rounded mb-1" /> : <div className="h-16 flex items-center justify-center text-gray-400">{busyCat === cat ? 'Uploading…' : existing ? 'Uploaded' : 'Upload'}</div>}
              <div className="font-semibold text-gray-700">{label}</div>
              <input type="file" className="hidden" accept={cat === 'video' ? 'video/*' : 'image/*'} onChange={(e) => upload(cat, e.target.files?.[0])} />
            </label>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] text-gray-500">{missing.length ? `Missing: ${missing.join(', ')} — you can still mark complete; it's recorded.` : 'All four photos present.'}</span>
        <Button size="sm" disabled={busy} onClick={() => complete()} className="bg-[#7A0B7A] hover:bg-[#5a085a]" data-testid="assist-s6-complete">{busy ? 'Saving…' : 'Mark complete'}</Button>
      </div>
    </div>
  );
}

export function Stage7Form({ userId, brand, data, notify, onSaved }) {
  const [mode, setMode] = useState(data.rating ? 'record' : 'send');
  const [f, setF] = useState({ customer_name: data.customer_name || '', customer_email: data.customer_email || '', rating: data.rating || 5, review_text: data.review_text || '' });
  const [busy, save] = useSave(() => assistApi.stage7(userId, brand, { mode, ...f, customer_email: f.customer_email || null, rating: mode === 'record' ? Number(f.rating) : null, notify }), onSaved, mode === 'send' ? 'Review link emailed to the customer' : 'Review recorded');
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-3" data-testid="assist-stage7-form">
      <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs font-semibold">
        {[['send', 'Send review link'], ['record', 'Record a review we received']].map(([m, l]) => (
          <button type="button" key={m} onClick={() => setMode(m)} data-testid={`assist-s7-mode-${m}`} className={`px-3 py-1.5 ${mode === m ? 'bg-[#27292E] text-white' : 'bg-white text-gray-700'}`}>{l}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Customer name"><Input required value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="assist-s7-name" /></Field>
        <Field label={mode === 'send' ? 'Customer email' : 'Customer email (optional)'}><Input type="email" required={mode === 'send'} value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} data-testid="assist-s7-email" /></Field>
        {mode === 'record' && (
          <>
            <Field label="Rating (1–5)"><Input type="number" min="1" max="5" value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} data-testid="assist-s7-rating" /></Field>
            <div className="sm:col-span-2"><Field label="Review"><Textarea required rows={3} value={f.review_text} onChange={(e) => setF({ ...f, review_text: e.target.value })} data-testid="assist-s7-text" /></Field></div>
          </>
        )}
      </div>
      {data.review_sent_at && !data.rating && <p className="text-[11px] text-sky-700">Review link sent {new Date(data.review_sent_at).toLocaleDateString()} — waiting for the customer.</p>}
      <SaveBar busy={busy} label={mode === 'send' ? 'Send link' : 'Save review'} testId="assist-s7-save" />
    </form>
  );
}
